import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { getAlunni, getFiltriClassi } from "@/lib/api";
import { formatCurrency } from "@/lib/utils";
import { useAuth } from "@/lib/AuthContext";
import { useAnnoScolastico } from "@/lib/annoScolastico";
import { useNavigate } from "react-router-dom";

// Tinte per causale: 1=Iscrizioni (blu), 2=Retta (viola), 3=Mensa (verde).
// Colore FORTE/saturo = addebito PAGATO; colore TENUE/chiaro = DA PAGARE.
// Solo i settori didattici principali in questa vista.
const SETTORI_AMMESSI = ["A", "E", "F", "M"];

const CAU_STYLES = {
  1: { label: "Iscrizioni", strongBg: "#1565c0", softBg: "#e3f0fb", softText: "#0d47a1" },
  2: { label: "Rette", strongBg: "#6a1b9a", softBg: "#f1e6f7", softText: "#4a148c" },
  3: { label: "Mensa", strongBg: "#2e7d32", softBg: "#e6f4e6", softText: "#1b5e20" },
};

function ContabileBadge({ b }) {
  const st = CAU_STYLES[b.cau] || { label: `Cau ${b.cau}`, strongBg: "#546e7a", softBg: "#eceff1", softText: "#37474f" };
  // Logica colore: MARCATO/forte = PAGATO, TENUE/chiaro = DA PAGARE.
  const style = b.pagato
    ? { background: st.strongBg, color: "white", fontWeight: 700, border: `1px solid ${st.strongBg}` }
    : { background: st.softBg, color: st.softText, fontWeight: 600, border: `1px solid ${st.softBg}` };
  const title = `${st.label} · ${formatCurrency(b.importo)} · scad. ${b.scadenza || "—"} · ${b.pagato ? "PAGATO" : (b.scaduto ? "DA PAGARE (scaduto)" : "DA PAGARE")}`;
  return (
    <span data-testid={`sc-badge-cau${b.cau}-${b.pagato ? "pag" : "nonpag"}`} title={title}
      style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", lineHeight: 1.15, padding: "2px 8px", borderRadius: 10, fontSize: 11, whiteSpace: "nowrap", ...style }}>
      <span>{formatCurrency(b.importo)}</span>
      {b.scadenza && <span style={{ fontSize: 9, opacity: 0.9 }}>⏱ {b.scadenza}</span>}
    </span>
  );
}

function scadMonth(scad) {
  const p = (scad || "").split("/");
  if (p.length !== 3) return { key: "9999-99", mm: 0, yy: 0, dd: 99 };
  const dd = parseInt(p[0], 10) || 0, mm = parseInt(p[1], 10) || 0, yy = parseInt(p[2], 10) || 0;
  return { key: `${yy}-${String(mm).padStart(2, "0")}`, mm, yy, dd };
}

// Matrice CREDITO: mesi dell'anno scolastico da Settembre a Giugno (sotto-colonne di Rette e Mensa).
const MESI_AS = [
  { mm: 9, lbl: "Set" }, { mm: 10, lbl: "Ott" }, { mm: 11, lbl: "Nov" }, { mm: 12, lbl: "Dic" },
  { mm: 1, lbl: "Gen" }, { mm: 2, lbl: "Feb" }, { mm: 3, lbl: "Mar" }, { mm: 4, lbl: "Apr" },
  { mm: 5, lbl: "Mag" }, { mm: 6, lbl: "Giu" },
];
const MESI_AS_SET = new Set(MESI_AS.map((x) => x.mm));

// Evidenziazione colonna del MESE CORRENTE (barra verticale ambra).
const CUR_MESE = new Date().getMonth() + 1; // 1..12
const CUR_YEAR = new Date().getFullYear();
const CUR_BORDER = "2px solid #ff9100";
const CUR_BG = "#fff4e5";       // banda celle corpo
const CUR_BG_HEAD = "#ffe0b2";  // header (opaco, copre il corpo durante lo scroll)

// Raggruppa i badge di una causale per mese di scadenza secondo l'anno scolastico.
// Slot: Set-Dic => anno, Gen-Giu => anno+1. Fuori slot (anno/mese non corrispondenti) => "altro".
function bucketByMonth(list, anno) {
  const map = {};
  (list || []).forEach((b) => {
    const { mm, yy } = scadMonth(b.scadenza);
    const expectedYear = (mm >= 9 && mm <= 12) ? anno : anno + 1;
    const key = (MESI_AS_SET.has(mm) && yy === expectedYear) ? mm : "altro";
    (map[key] = map[key] || []).push(b);
  });
  Object.values(map).forEach((arr) => arr.sort((x, y) => scadMonth(x.scadenza).dd - scadMonth(y.scadenza).dd));
  return map;
}

export default function SituazioniContabiliPage() {
  const { annoCorrente } = useAuth();
  const { anno: annoGlobal, formatAs } = useAnnoScolastico();
  const anno = Number.isFinite(annoGlobal) ? annoGlobal : annoCorrente;
  const navigate = useNavigate();
  const [settoriList, setSettoriList] = useState([]);
  const [classiList, setClassiList] = useState([]);
  const [filtroSettore, setFiltroSettore] = useState("");
  const [filtroClasse, setFiltroClasse] = useState("");
  const [search, setSearch] = useState("");
  const [sortMode, setSortMode] = useState("nome");
  const [tab, setTab] = useState("rette"); // "rette" = Iscrizione+Rette, "mensa" = Mensa
  const [items, setItems] = useState([]);
  const [badges, setBadges] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const requestRef = useRef(0);

  // Selezione alunni (spunta per riga). Persistita in sessionStorage così resta attiva
  // anche navigando (es. verso Comunicazioni per l'invio dei solleciti).
  const [selected, setSelected] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem("sc_selected_cods_v1") || "[]")); } catch { return new Set(); }
  });
  useEffect(() => {
    try { sessionStorage.setItem("sc_selected_cods_v1", JSON.stringify([...selected])); } catch { /* ignore */ }
  }, [selected]);

  useEffect(() => {
    if (!anno) return;
    getFiltriClassi(anno).then(r => {
      setSettoriList(r.data?.settori || []);
      setClassiList(r.data?.classi || []);
    }).catch(() => { });
    setFiltroSettore(""); setFiltroClasse("");
  }, [anno]);

  const carica = useCallback(async () => {
    if (!anno) return;
    const reqId = ++requestRef.current;
    setLoading(true); setErr(null);
    try {
      const params = { anno, limit: 5000, page: 1, sort_by: "classe", sort_dir: "asc" };
      params.settore = filtroSettore || SETTORI_AMMESSI.join(",");
      if (filtroClasse) params.classe_nome = filtroClasse;
      if (search) params.search = search;
      const r = await getAlunni(params);
      if (reqId !== requestRef.current) return;
      const rows = r.data?.data || r.data?.items || [];
      setItems(rows);
      if (rows.length > 0) {
        try {
          const br = await api.post("/alunni/contabile-badges", { cods: rows.map(a => a.Cod), anno: Number(anno) });
          if (reqId === requestRef.current) setBadges(br.data?.badges || {});
        } catch { /* ignore */ }
      } else setBadges({});
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Errore caricamento");
    } finally { if (reqId === requestRef.current) setLoading(false); }
  }, [anno, filtroSettore, filtroClasse, search]);

  useEffect(() => { carica(); }, [carica]);

  const sortedItems = useMemo(() => {
    const coll = (x, y) => (x || "").localeCompare(y || "", "it", { sensitivity: "base" });
    const arr = [...items];
    if (sortMode === "settore") {
      arr.sort((a, b) => coll(a.settore, b.settore) || coll(a.classe, b.classe) || coll(a.Cog, b.Cog) || coll(a.Nom, b.Nom));
    } else {
      arr.sort((a, b) => coll(a.Cog, b.Cog) || coll(a.Nom, b.Nom));
    }
    return arr;
  }, [items, sortMode]);

  // Colonne mese della matrice: aggiungo "Altro" (scadenze fuori Set-Giu) solo se presente nei dati.
  const { hasAltroRette, hasAltroMensa } = useMemo(() => {
    let r = false, m = false;
    Object.values(badges || {}).forEach((list) => {
      (list || []).forEach((b) => {
        const { mm, yy } = scadMonth(b.scadenza);
        const expectedYear = (mm >= 9 && mm <= 12) ? anno : anno + 1;
        const inSlot = MESI_AS_SET.has(mm) && yy === expectedYear;
        if (!inSlot) { if (b.cau === 2) r = true; else if (b.cau === 3) m = true; }
      });
    });
    return { hasAltroRette: r, hasAltroMensa: m };
  }, [badges, anno]);
  const mesiRette = useMemo(() => (hasAltroRette ? [...MESI_AS, { mm: "altro", lbl: "Altro" }] : MESI_AS), [hasAltroRette]);
  const mesiMensa = useMemo(() => (hasAltroMensa ? [...MESI_AS, { mm: "altro", lbl: "Altro" }] : MESI_AS), [hasAltroMensa]);
  const totalCols = 8 + (tab === "rette" ? 1 + mesiRette.length : mesiMensa.length);

  // Mese corrente evidenziato solo se la sua colonna appartiene all'anno di calendario corrente.
  const isCurMonth = (mm) => {
    if (mm === "altro") return false;
    const slotYear = (mm >= 9 && mm <= 12) ? anno : anno + 1;
    return mm === CUR_MESE && slotYear === CUR_YEAR;
  };
  // Anno (2 cifre) della colonna mese secondo l'anno scolastico: Set-Dic = anno, Gen-Giu = anno+1.
  const yy2 = (mm) => (mm === "altro" ? "" : String((mm >= 9 && mm <= 12) ? anno : anno + 1).slice(-2));
  const headHi = (mm, i) => (isCurMonth(mm)
    ? { borderLeft: CUR_BORDER, borderRight: CUR_BORDER, background: CUR_BG_HEAD, color: "#e65100", fontWeight: 800 }
    : { borderLeft: i === 0 ? "2px solid #cfd8dc" : "1px solid #eceff1" });
  const cellHi = (mm, i) => (isCurMonth(mm)
    ? { borderLeft: CUR_BORDER, borderRight: CUR_BORDER, background: CUR_BG }
    : { borderLeft: i === 0 ? "2px solid #cfd8dc" : "1px solid #f0f2f3" });

  const ellipsis = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

  const toggleSel = (cod) => setSelected((prev) => { const n = new Set(prev); if (n.has(cod)) n.delete(cod); else n.add(cod); return n; });
  const toggleAllVisible = () => setSelected((prev) => {
    const n = new Set(prev);
    const allSel = sortedItems.length > 0 && sortedItems.every((a) => n.has(a.Cod));
    sortedItems.forEach((a) => { if (allSel) n.delete(a.Cod); else n.add(a.Cod); });
    return n;
  });
  const allVisibleSelected = sortedItems.length > 0 && sortedItems.every((a) => selected.has(a.Cod));
  const inviaSolleciti = () => {
    const cods = [...selected];
    if (!cods.length) return;
    navigate(`/g/comunicator/comunicator?codes=${cods.join(",")}&anno=${anno}&label=${encodeURIComponent("Situazioni Contabili")}`);
  };

  return (
    <div>
      <div className="sw-page-title">
        <h1>💳 Situazioni Contabili</h1>
        <div className="sw-breadcrumb">Home &gt; Credito &gt; Situazioni Contabili{anno ? ` · A.S. ${formatAs ? formatAs(anno) : anno}` : ""}</div>
      </div>

      <div style={{ margin: "0 20px 8px", display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center", fontSize: 11, color: "#546e7a" }} data-testid="sc-legend">
        {[1, 2, 3].map(c => (
          <span key={c} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: CAU_STYLES[c].strongBg, display: "inline-block" }} />
            <b>{CAU_STYLES[c].label}</b>
          </span>
        ))}
        <span style={{ marginLeft: 8 }}>Colore forte = <b>pagato</b> · Colore chiaro = <b>da pagare</b></span>
      </div>

      <div className="sw-filters" style={{ margin: "0 20px 10px", display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <input data-testid="sc-search" className="sw-filter-input" placeholder="cerca cognome, nome, CF..." value={search}
          onChange={e => setSearch(e.target.value)} style={{ minWidth: 220 }} />
        <select data-testid="sc-settore" className="sw-filter-select" value={filtroSettore} onChange={e => setFiltroSettore(e.target.value)}>
          <option value="">Tutti (A/E/F/M)</option>
          {settoriList.filter(s => SETTORI_AMMESSI.includes(s)).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="sc-classe" className="sw-filter-select" value={filtroClasse} onChange={e => setFiltroClasse(e.target.value)}>
          <option value="">Qualsiasi classe</option>
          {classiList.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="sc-sort" className="sw-filter-select" value={sortMode} onChange={e => setSortMode(e.target.value)} title="Ordina alunni">
          <option value="nome">Ordina: Cognome e Nome</option>
          <option value="settore">Ordina: Settore · Classe · Cognome e Nome</option>
        </select>
        {(search || filtroSettore || filtroClasse) && <button className="sw-filter-btn" data-testid="sc-clear" onClick={() => { setSearch(""); setFiltroSettore(""); setFiltroClasse(""); }}>Pulisci</button>}
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#607d8b" }} data-testid="sc-count">{items.length} alunni</span>
      </div>

      {err && <div style={{ margin: "0 20px 12px", padding: 10, background: "#fff0f0", color: "#c00", fontSize: 12, border: "1px solid #fcc" }}>{err}</div>}

      <div style={{ margin: "0 20px 10px", display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", background: selected.size ? "#fff3e0" : "#f5f5f5", border: `1px solid ${selected.size ? "#ffb74d" : "#e0e0e0"}`, borderRadius: 4 }} data-testid="sc-selbar">
        <span style={{ fontSize: 12, fontWeight: 800, color: selected.size ? "#e65100" : "#90a4ae" }} data-testid="sc-sel-count">
          ✓ {selected.size} alunni selezionati
        </span>
        <button onClick={inviaSolleciti} disabled={!selected.size} data-testid="sc-invia-solleciti"
          style={{ padding: "6px 14px", background: selected.size ? "#c62828" : "#e0e0e0", color: selected.size ? "white" : "#9e9e9e", border: "none", fontSize: 12, fontWeight: 800, cursor: selected.size ? "pointer" : "default", borderRadius: 4 }}>
          📨 Invia solleciti ai selezionati →
        </button>
        {selected.size > 0 && (
          <button onClick={() => setSelected(new Set())} data-testid="sc-clear-sel"
            style={{ padding: "6px 10px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 4 }}>
            ✕ Deseleziona tutti
          </button>
        )}
        <span style={{ fontSize: 11, color: "#90a4ae", marginLeft: "auto" }}>La selezione resta attiva e viene portata in Comunicazioni per i solleciti</span>
      </div>

      <div style={{ margin: "0 20px 0", display: "flex", gap: 4, alignItems: "flex-end" }} data-testid="sc-tabs">
        <button onClick={() => setTab("rette")} data-testid="sc-tab-rette"
          style={{ padding: "8px 20px", fontSize: 13, fontWeight: 800, cursor: "pointer", border: "2px solid #6a1b9a", borderBottom: "none", background: tab === "rette" ? "#6a1b9a" : "white", color: tab === "rette" ? "white" : "#6a1b9a", borderRadius: "6px 6px 0 0" }}>
          Iscrizione e Rette
        </button>
        <button onClick={() => setTab("mensa")} data-testid="sc-tab-mensa"
          style={{ padding: "8px 20px", fontSize: 13, fontWeight: 800, cursor: "pointer", border: "2px solid #2e7d32", borderBottom: "none", background: tab === "mensa" ? "#2e7d32" : "white", color: tab === "mensa" ? "white" : "#2e7d32", borderRadius: "6px 6px 0 0" }}>
          Mensa
        </button>
      </div>

      <div className="sw-table-container" style={{ maxHeight: "70vh", overflow: "auto", margin: "0 20px" }}>
        <table className="sw-table" data-testid="sc-table">
          <thead>
            <tr>
              <th rowSpan={2} style={{ width: 34, textAlign: "center" }} title="Seleziona/deseleziona tutti gli alunni visibili">
                <input type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} data-testid="sc-sel-all" style={{ cursor: "pointer" }} />
              </th>
              <th rowSpan={2} style={{ width: 60 }}>Cod</th>
              <th rowSpan={2} style={{ width: 220 }}>Cognome Nome / CF</th>
              <th rowSpan={2} style={{ width: 44, textAlign: "center" }}>Ciclo</th>
              <th rowSpan={2} style={{ width: 46 }}>Set.</th>
              <th rowSpan={2} style={{ width: 66 }}>Classe</th>
              <th rowSpan={2} style={{ width: 110 }}>Telefono</th>
              <th rowSpan={2} style={{ width: 58, textAlign: "center" }}>Mov.</th>
              {tab === "rette" ? (
                <>
                  <th rowSpan={2} style={{ width: 90, minWidth: 90, borderLeft: "2px solid #cfd8dc" }} data-testid="sc-colhead-cau1">
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 2, background: CAU_STYLES[1].strongBg, display: "inline-block" }} />
                      {CAU_STYLES[1].label}
                    </span>
                  </th>
                  <th colSpan={mesiRette.length} style={{ textAlign: "center", borderLeft: "2px solid #cfd8dc", color: CAU_STYLES[2].strongBg }} data-testid="sc-colhead-cau2">
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 2, background: CAU_STYLES[2].strongBg, display: "inline-block" }} />
                      Rette
                    </span>
                  </th>
                </>
              ) : (
                <th colSpan={mesiMensa.length} style={{ textAlign: "center", borderLeft: "2px solid #cfd8dc", color: CAU_STYLES[3].strongBg }} data-testid="sc-colhead-cau3">
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 2, background: CAU_STYLES[3].strongBg, display: "inline-block" }} />
                    {CAU_STYLES[3].label}
                  </span>
                </th>
              )}
            </tr>
            <tr>
              {tab === "rette"
                ? mesiRette.map((mo, i) => (
                  <th key={`rh-${mo.mm}`} style={{ width: 80, minWidth: 80, fontSize: 10, top: 28, textAlign: "center", ...headHi(mo.mm, i) }} data-testid={`sc-subhead-cau2-${mo.mm}`}>{mo.mm === "altro" ? mo.lbl : `${mo.lbl} ${yy2(mo.mm)}`}</th>
                ))
                : mesiMensa.map((mo, i) => (
                  <th key={`mh-${mo.mm}`} style={{ width: 80, minWidth: 80, fontSize: 10, top: 28, textAlign: "center", ...headHi(mo.mm, i) }} data-testid={`sc-subhead-cau3-${mo.mm}`}>{mo.mm === "altro" ? mo.lbl : `${mo.lbl} ${yy2(mo.mm)}`}</th>
                ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={totalCols} className="sw-loading">Caricamento...</td></tr>
            ) : sortedItems.length === 0 ? (
              <tr><td colSpan={totalCols} className="sw-empty"><p>Nessun alunno trovato</p></td></tr>
            ) : sortedItems.map((a) => {
              const bs = badges[a.Cod] || badges[String(a.Cod)] || [];
              const iscr = bs.filter((x) => x.cau === 1);
              const bRette = bucketByMonth(bs.filter((x) => x.cau === 2), anno);
              const bMensa = bucketByMonth(bs.filter((x) => x.cau === 3), anno);
              return (
                <tr key={`${a.Cod}-${a.anno}`} data-testid={`sc-row-${a.Cod}`} style={{ background: selected.has(a.Cod) ? "#fff8e1" : undefined }}>
                  <td style={{ textAlign: "center", padding: "1px 2px" }}>
                    <input type="checkbox" checked={selected.has(a.Cod)} onChange={() => toggleSel(a.Cod)} data-testid={`sc-sel-${a.Cod}`} style={{ cursor: "pointer" }} />
                  </td>
                  <td style={{ padding: "1px 4px", whiteSpace: "nowrap" }}>{a.Cod}</td>
                  <td style={{ padding: "1px 6px", overflow: "hidden" }} title={`${a.Cog || ""} ${a.Nom || ""}\n${a.CodFis || ""}`.trim()}>
                    <div style={{ fontWeight: 600, ...ellipsis }}>{a.Cog} {a.Nom}</div>
                    <div style={{ fontSize: 9, color: "#777", fontFamily: "Consolas,monospace", ...ellipsis }}>{a.CodFis || "-"}</div>
                  </td>
                  <td className="center-col" style={{ padding: "1px 2px" }}>{a.ciclo_curriculum || "—"}</td>
                  <td className="center-col">{a.settore || "-"}</td>
                  <td style={ellipsis} title={a.classe || "-"}>{a.classe || "-"}</td>
                  <td style={{ fontSize: 10, ...ellipsis }} title={a.Tel1 || a.Tel2 || "-"}>{a.Tel1 || a.Tel2 || "-"}</td>
                  <td style={{ textAlign: "center", padding: "1px 2px" }}>
                    <button onClick={() => navigate(`/g/segremat/movimenti?codice_alunno=${a.Cod}&anno=${anno}&back=${encodeURIComponent("/g/credito/credito_situazioni")}&back_label=${encodeURIComponent("Torna a Situazioni Contabili")}`)}
                      data-testid={`sc-mov-${a.Cod}`} title="Apri la lista movimenti di questo alunno"
                      style={{ padding: "2px 7px", background: "white", color: "#1565c0", border: "1px solid #1565c0", fontSize: 10, fontWeight: 800, cursor: "pointer", borderRadius: 3, whiteSpace: "nowrap" }}>
                      📋 Mov.
                    </button>
                  </td>
                  {tab === "rette" ? (
                    <>
                      {/* Iscrizioni (nessuna sotto-colonna) */}
                      <td style={{ padding: "3px 6px", verticalAlign: "top", width: 90, borderLeft: "2px solid #cfd8dc" }} data-testid={`sc-cell-cau1-${a.Cod}`}>
                        {iscr.length ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 3, alignItems: "flex-start" }}>
                            {iscr.map((b, i) => <ContabileBadge key={i} b={b} />)}
                          </div>
                        ) : null}
                      </td>
                      {/* Rette per mese di scadenza */}
                      {mesiRette.map((mo, i) => {
                        const cells = bRette[mo.mm] || [];
                        return (
                          <td key={`r-${mo.mm}`} style={{ padding: "2px 3px", verticalAlign: "top", textAlign: "center", ...cellHi(mo.mm, i) }} data-testid={`sc-cell-cau2-${mo.mm}-${a.Cod}`}>
                            {cells.length ? (
                              <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
                                {cells.map((b, j) => <ContabileBadge key={j} b={b} />)}
                              </div>
                            ) : null}
                          </td>
                        );
                      })}
                    </>
                  ) : (
                    /* Mensa per mese di scadenza */
                    mesiMensa.map((mo, i) => {
                      const cells = bMensa[mo.mm] || [];
                      return (
                        <td key={`m-${mo.mm}`} style={{ padding: "2px 3px", verticalAlign: "top", textAlign: "center", ...cellHi(mo.mm, i) }} data-testid={`sc-cell-cau3-${mo.mm}-${a.Cod}`}>
                          {cells.length ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
                              {cells.map((b, j) => <ContabileBadge key={j} b={b} />)}
                            </div>
                          ) : null}
                        </td>
                      );
                    })
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
